import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import styled from "styled-components";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import {
  startOfWeek,
  addYears,
  subYears,
  addDays,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  startOfYear,
  addMonths,
  isSaturday,
  isSunday,
  parse
} from "date-fns";

import { AgendaAPI } from "@api";
import { ButtonCircle } from ".";
import { ChevronLeft, ChevronRight } from "../icons";
import { formatDate } from "@helpers";

const AgendaContainer = styled.div`
  padding: 0.75em;
  height: 100%;
  width: 100%;
  display: inline-grid;
  border-radius: 0.25em;
`;

const CalendarContainer = styled.div`
  padding: 0.75em;
`;

const Container = styled.div`
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
  border-radius: 0.25em;
  margin: 0.25em;
  overflow: hidden;
`;

const Month = styled.div`
  &:hover {
    font-weight: bold;
    cursor: pointer;
    color: #ffc107;
  }

  &.selected {
    color: #ffc107;
  }
`;

const DateCell = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 25px;
  margin: 0.25em;
  font-size: 14px;
  line-height: 1.33;
  border: none;
  background-color: transparent;

  &:hover {
    opacity: 0.8;
    cursor: pointer;
    font-weight: bold;
  }

  &.selected {
    border: 1px solid #f2a900;
    background-color: rgba(242, 169, 0, 0.1);
  }

  &.disabled {
    opacity: 0.2;
  }

  &.sunday {
    color: rgba(235, 54, 54, 1);
  }
`;

const Table = styled.table`
  & th {
    font-weight: 500 !important;
    border-bottom: rgba(0, 0, 0, 0.1) 1px solid;
    font-size: 0.9em;
    text-align: center;
  }

  & td {
    font-weight: 500 !important;
    font-size: 0.9em;
    text-align: center;
    height: 2.5em;
  }
`;

const Calendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [agendas, setAgendas] = useState([]);
  const { t } = useTranslation();

  const getAgendas = async () => {
    const from = formatDate(startOfMonth(currentMonth), "yyyy-M-dd")
    const to = formatDate(endOfMonth(currentMonth), "yyyy-M-dd")
    const filter = {
      from: from,
      to: to,
      limit: 100,
    };
    const { data } = await AgendaAPI(queryString.stringify(filter));

    setAgendas(data.data != null ? data.data.result : []);
  };

  useEffect(() => {
    getAgendas();
  }, [currentMonth]);

  const onDateClick = day => {
    setSelectedDate(day);
  };

  const nextYear = () => {
    setCurrentMonth(startOfYear(addYears(currentMonth, 1)));
  };

  const prevYear = () => {
    setCurrentMonth(startOfYear(subYears(currentMonth, 1)));
  };

  const handleMonth = (month) => {
    setCurrentMonth(month);
  };

  const isHaveAgenda = (day) => {
    return agendas.find(ag => {
      const agendaDate = parse(ag.event_datetime, "dd-M-yyyy HH:mm:ss", new Date());
      return isSameDay(agendaDate, day)
    });
  };

  const renderAgenda = () => {
    const year = formatDate(selectedDate, "yyyy");
    const month = formatDate(selectedDate, "MMMM");
    const date = formatDate(selectedDate, "dd");
    const day = formatDate(selectedDate, "EEEE");
    const agenda = agendas.find(ag => {
      const agendaDate = parse(ag.event_datetime, "dd-M-yyyy HH:mm:ss", new Date());
      return isSameDay(agendaDate, selectedDate)
    });
    return (
      <AgendaContainer>
        <Row className="justify-content-between">
          <Col className="col-auto mr-auto font-small">
            {month}
          </Col>
          <Col className="col-auto font-small">{year}</Col>
        </Row>
        <Row>
          <Col xs={12}>
            <div className="font-extra-big">{date}</div>
            <div>{day}</div>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>{agenda ? agenda.title : t('no_agenda')}</Col>
        </Row>
      </AgendaContainer>
    )
  }

  const renderHeader = () => {
    const dateFormat = "MMM";
    let rows = [];
    let months = [];
    let startMonth = startOfYear(currentMonth);

    for (let i = 0; i < 2; i++) {
      for (let j = 0; j < 6; j++) {
        const cloneMonth = addMonths(startMonth, i * 6 + j);
        months.push(
          <Col xs={2} key={j}>
            <Month 
              className={isSameMonth(cloneMonth, currentMonth) && "selected"}
              onClick={() => handleMonth(cloneMonth)}
            >
              {formatDate(cloneMonth, dateFormat)}
            </Month>
          </Col>
        );
      }
      rows.push(
       <Row key={i}>
         {months}
       </Row> 
      )
      months = [];
    }

    return (
      <Row className="justify-content-between pb-5">
        <Col className="col-auto mr-auto font-small p-0">
          {rows}
        </Col>
        <Col className="col-auto d-flex align-items-center">
          <ButtonCircle onClick={prevYear}>
            <ChevronLeft />
          </ButtonCircle>
          <div className="px-2">{formatDate(currentMonth, "yyyy")}</div>
          <ButtonCircle onClick={nextYear}>
            <ChevronRight />
          </ButtonCircle>
        </Col>
      </Row>
    )
  };

  const renderHeaderMobile = () => {
    const dateFormat = "MMM";
    let rows = [];
    let months = [];
    let startMonth = startOfYear(currentMonth);

    for (let i = 0; i < 2; i++) {
      for (let j = 0; j < 6; j++) {
        const cloneMonth = addMonths(startMonth, i * 6 + j);
        months.push(
          <Col xs={2} key={j}>
            <Month 
              className={isSameMonth(cloneMonth, currentMonth) && "selected"}
              onClick={() => handleMonth(cloneMonth)}
            >
              {formatDate(cloneMonth, dateFormat)}
            </Month>
          </Col>
        );
      }
      rows.push(
       <Row key={i}>
         {months}
       </Row> 
      )
      months = [];
    }

    return (
      <Row className="justify-content-center pb-5">
        <Col className="col-auto mr-auto font-small p-0">
          {rows}          
        </Col>
        <Col className="col-auto d-flex align-items-center">
          <ButtonCircle onClick={prevYear}>
            <ChevronLeft />
          </ButtonCircle>
          <div className="px-2">{formatDate(currentMonth, "yyyy")}</div>
          <ButtonCircle onClick={nextYear}>
            <ChevronRight />
          </ButtonCircle>
        </Col>
      </Row>
    )
  };

  const renderDays = () => {
    const dateFormat = "EEE";
    const days = [];
    let startDate = startOfWeek(currentMonth);

    for (let i = 0; i < 7; i++) {
      days.push(
        <th key={i}>
          {formatDate(addDays(startDate, i), dateFormat)}
        </th>
      );
    }

    return (
      <tr>{days}</tr>
    )
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const dateFormat = "d";
    const rows = [];

    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = formatDate(day, dateFormat);
        const cloneDay = day;
        days.push(
          <td key={i}>
            <DateCell
              className={`${
                !isSameMonth(day, monthStart)
                  ? "disabled"
                  : isHaveAgenda(day, selectedDate) ? "selected" : ""
              } ${(isSaturday(day) || isSunday(day)) && 'sunday'}`}
              key={day}
              onClick={() => {
                if (isSameMonth(cloneDay, monthStart)) {
                  onDateClick(cloneDay)
                }
              }}
            >
              {formattedDate}
            </DateCell>
          </td>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <tr key={day}>
          {days}
        </tr>
      );
      days = [];
    }
    return rows;
  };

  return (
    <Container>
      <Row>
        <Col md={4} className="bg-brown text-light p-4">
          {renderAgenda()}
        </Col>
        <Col md={8} className="p-4">
          <CalendarContainer>
            <div className="d-none d-sm-block">
              {renderHeader()}
            </div>

            <div className="d-md-none">
              {renderHeaderMobile()}
            </div>

            <Row>
              <Table>
                <thead>
                  {renderDays()}
                </thead>
                <tbody>
                  {renderCells()}
                </tbody>
              </Table>
            </Row>
          </CalendarContainer>
        </Col>
      </Row>
    </Container>
  );
};

export default Calendar;
